import { List } from 'antd';
import get from 'lodash/get';
import { useState, useRef } from 'react';
import { useApolloClient, useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { queryReferralsByContactIdIndex } from 'src/_shared/api/graphql/custom/referrals/';
import Spinner from 'src/_shared/components/spinner/SpinnerComponent.jsx';
import { ml } from 'src/_shared/services/utils';
import { ReferralCardJob as ReferralCard } from '../_shared/components/referral-card/ReferralCardContainer.jsx';
import { EditableReferralDetailsCard } from '../_shared/components/referral-detail/ReferralDetailCardComponent.jsx';

const UPDATE_REFERRAL_STATUS = gql`
	mutation UpdateReferral($input: UpdateReferralInput!) {
		updateReferral(input: $input) {
			id
			contactId
			userId
			jobId
			bonusStatus
			status
			customStatus
			atsId
			companyId
			status
			note
			message
			ownershipAtsId
			referralDate
			referralBonusNotificationSent
			hireDate
			questionsData
			candidateQuestionsData
			interestedQuestionsData
			diversityHire
		}
	}
`;

const DELETE_REFERRAL = gql`
	mutation DeleteReferral($input: DeleteReferralInput!) {
		deleteReferral(input: $input) {
			id
			contactId
			userId
			jobId
		}
	}
`;

const DetailsView = (props) => {
	const client = useApolloClient();
	const listContainerRef = useRef(null);
	const [company, setCompany] = useState(get(props, 'currentUser.company'));
	const [currencyRate, setCurrencyRate] = useState(1);
	const [currencySymbol, setCurrencySymbol] = useState('$');
	const [referrals, setReferrals] = useState([]);

	const contactId = props.referral?.contact?.id;

	const [updateReferralStatus] = useMutation(UPDATE_REFERRAL_STATUS);
	const [deleteReferralMutation] = useMutation(DELETE_REFERRAL);

	const { data, loading, error, fetchMore, refetch } = useQuery(
		queryReferralsByContactIdIndex,
		{
			variables: {
				contactId: contactId,
				first: 10,
				after: null,
			},
			skip: !contactId,
			fetchPolicy: 'network-only',
			onCompleted: (data) => {
				setReferrals(data?.queryReferralsByContactIdIndex?.items);
			},
			onError: (error) => {
				console.error('QueryReferralsByContactIdIndex query error:', error);
			},
		}
	);

	const loadMoreReferrals = () => {
		if (data?.queryReferralsByContactIdIndex?.nextToken && !loading) {
			fetchMore({
				variables: {
					after: data.queryReferralsByContactIdIndex.nextToken,
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) return prev;

					const updatedResult = {
						queryReferralsByContactIdIndex: {
							...fetchMoreResult.queryReferralsByContactIdIndex,
							items: [
								...prev.queryReferralsByContactIdIndex.items,
								...fetchMoreResult.queryReferralsByContactIdIndex.items,
							],
						},
					};

					setReferrals(updatedResult.queryReferralsByContactIdIndex.items);

					return updatedResult;
				},
			});
		}
	};

	const onScroll = (e) => {
		if (loading) return;

		const tolerance = 100;
		const { scrollHeight, scrollTop, clientHeight } = e.currentTarget;
		const bottomReached =
			scrollHeight - Math.ceil(scrollTop + clientHeight) <= tolerance;

		if (
			bottomReached &&
			data?.queryReferralsByContactIdIndex?.nextToken &&
			!loading
		) {
			loadMoreReferrals();
		}
	};

	const deleteReferral = async (referral) => {
		try {
			const { data: mutationData } = await deleteReferralMutation({
				variables: {
					input: {
						id: referral.id,
					},
				},
			});

			if (mutationData?.deleteReferral) {
				const remainingReferrals = referrals.filter(
					(ref) => ref.id !== referral.id
				);

				if (remainingReferrals.length > 0) {
					setReferrals(remainingReferrals);
					refetch();
				} else {
					props.history.push('/referrals');
				}
			}
		} catch (error) {
			console.error('Error deleting referral:', error);
		}
	};

	const goBack = () => {
		props.history.push('/referrals');
	};

	const handleUpdateReferral = async ({ input }) => {
		try {
			const { data: mutationData } = await updateReferralStatus({
				variables: { input },
			});

			if (mutationData?.updateReferral) {
				const updatedReferrals = referrals.map((ref) =>
					ref.id === mutationData.updateReferral.id
						? { ...ref, ...mutationData.updateReferral }
						: ref
				);
				setReferrals(updatedReferrals);
				refetch();
			}
		} catch (error) {
			console.error('Error updating referral:', error);
		}
	};

	const {
		allMultiLingualData,
		currentUser,
		addGeneralReferralText,
		addJobHistoryText,
		cancelAddText,
		companyText,
		confirmNewPositionText,
		descriptionText,
		downloadResumeText,
		editProfileText,
		endDatesText,
		mobileText,
		myContactsText,
		organizationText,
		ownershipSettings,
		positionText,
		referredByText,
		referralCommentsText,
		referralReadyText,
		referralsText,
		referredOnText,
		resumeText,
		showJobHistoryText,
		startDateText,
		statusText,
		tieredBonusText,
	} = props;
	return (
		<main>
			<span className="custom-back-btn" onClick={goBack}>
				<i className="icon-arrow-left" />
				{ml('Back', currentUser, allMultiLingualData)}
			</span>
			<div className="referrrals-profile">
				<EditableReferralDetailsCard
					key="referral"
					referralNote
					addGeneralReferralText={addGeneralReferralText}
					addJobHistoryText={addJobHistoryText}
					cancelAddText={cancelAddText}
					companyText={companyText}
					confirmNewPositionText={confirmNewPositionText}
					allMultiLingualData={allMultiLingualData}
					currentUser={currentUser}
					descriptionText={descriptionText}
					downloadResumeText={downloadResumeText}
					edit={props.edit ? props.edit : false}
					editProfileText={editProfileText}
					employee={props.employeeData}
					endDatesText={endDatesText}
					mobileText={mobileText}
					myContactsText={myContactsText}
					organizationText={organizationText}
					positionText={positionText}
					referral={props.referral}
					contact={props.referral}
					referrals={referrals}
					referralCommentsText={referralCommentsText}
					referralReadyText={referralReadyText}
					resumeText={resumeText}
					showJobHistoryText={showJobHistoryText}
					startDateText={startDateText}
					tieredBonusText={tieredBonusText}
					onUpdate={props.onUpdate}
					onUpdateContact={props.onUpdateContact}
					onUpdateReferral={handleUpdateReferral}
				/>
			</div>
			<h4 className="referrrals-details-heading">
				{ml('Referrals', currentUser, allMultiLingualData)}
			</h4>

			{data?.queryReferralsByContactIdIndex?.items || referrals ? (
				<div
					ref={listContainerRef}
					onScroll={onScroll}
					style={{
						maxHeight: '70vh',
						overflowY: 'auto',
						padding: '0 10px',
					}}
				>
					<List
						className="rd-wrap"
						grid={{ gutter: 14, xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 3 }}
						dataSource={referrals}
						itemLayout="horizontal"
						renderItem={(referral) => (
							<List.Item>
								<ReferralCard
									key={referral?.referralId || referral?.id}
									currentCurrencyRate={currencyRate}
									currentCurrencySymbol={currencySymbol}
									allMultiLingualData={allMultiLingualData}
									currentUser={currentUser}
									deleteReferral={deleteReferral}
									employee={props.employeeData}
									ownershipSettings={ownershipSettings}
									referral={referral}
									referredByText={referredByText}
									referredOnText={referredOnText}
									statusText={statusText}
									onUpdateJob={props.onUpdateJob}
									onUpdateReferral={handleUpdateReferral}
									client={client}
								/>
							</List.Item>
						)}
					/>
					{data?.queryReferralsByContactIdIndex?.nextToken && loading && (
						<div
							style={{ textAlign: 'center', marginTop: 20, marginBottom: 20 }}
						>
							<Spinner company={company} />
						</div>
					)}
				</div>
			) : (
				<Spinner company={company} />
			)}
		</main>
	);
};

export default DetailsView;
